import { ReactNode, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from '@braskai/icons';
import { Button, ModalWrapper, useDeviceType } from '@braskai/ui-kit';

import { Close } from '@mui/icons-material';
import { Box, Drawer, Modal, Stack, Typography } from '@mui/material';

import { useProducts } from '@/entities/pricing';

import { API } from '@/shared/api';
import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { MixpanelEvents } from '@/shared/thirdParty';
import { PaymentInterval, Plan } from '@/shared/types/Billing';
import { ModalHeaderDefault } from '@/shared/ui/modal';

import { PLANS, TrialPlan } from '../model/PLANS';
import { useTrialPaywallModalStore } from '../model/useTrialPaywallModalStore';
import { PlanOption } from './PlanOption';

type TrialFeature = {
  name: string;
  description: string;
  icon: ReactNode;
};

const DEFAULT_TITLE = 'Unlock all download options';

const FeatureItem = ({ feature, showDescription = true }: { feature: TrialFeature; showDescription?: boolean }) => (
  <Stack key={feature.name} flexDirection="column">
    <Stack direction="row" gap={10}>
      <Stack marginTop={2} sx={{ color: 'black' }}>
        {feature.icon}
      </Stack>
      <Stack direction="column">
        <Typography variant="body-2-md">{feature.name}</Typography>
        {showDescription && (
          <Typography color="grey.500" variant="body-2">
            {feature.description}
          </Typography>
        )}
      </Stack>
    </Stack>
  </Stack>
);

export const PlanOptionsSection = ({
  selectedPlanName,
  handleSelectPlan,
}: {
  selectedPlanName: null | TrialPlan['name'];
  handleSelectPlan: (planName: TrialPlan['name']) => void;
}) => (
  <>
    {PLANS.map((plan: TrialPlan) => (
      <PlanOption
        key={plan.name}
        name={plan.name}
        title={plan.title || ''}
        chipLabel={plan.chipLabel || ''}
        features={plan.features}
        isSelected={plan.name === selectedPlanName}
        minutes={plan.minutes || 0}
        price={plan.price}
        onSelectPlan={handleSelectPlan}
      />
    ))}
  </>
);

const PreviousFeaturesSection = ({
  expandPreviousFeatures,
  selectedPlan,
  setExpandPreviousFeatures,
}: {
  expandPreviousFeatures: boolean;
  selectedPlan: TrialPlan | undefined;
  setExpandPreviousFeatures: (expand: boolean) => void;
}) => {
  if (!selectedPlan?.previousFeatures) return null;

  return (
    <>
      <Stack
        alignItems="center"
        direction="row"
        gap={4}
        sx={{ cursor: 'pointer' }}
        onClick={() => setExpandPreviousFeatures(!expandPreviousFeatures)}
      >
        <Typography variant="body-2-md">{selectedPlan.previousFeatures.name}</Typography>
        {expandPreviousFeatures ? <ChevronUpIcon height={16} width={16} /> : <ChevronDownIcon height={16} width={16} />}
      </Stack>
      {expandPreviousFeatures && (
        <Stack direction="column" gap={12}>
          {selectedPlan.previousFeatures.features.map((feature) => (
            <FeatureItem key={feature.name} feature={feature} showDescription={false} />
          ))}
        </Stack>
      )}
    </>
  );
};

const planNameMapping: Record<string, string> = {
  Business: 'Business 500',
  Creator: 'Creator',
  'Creator Pro': 'Creator Pro 100',
};

const TrialPaywallComponent = () => {
  const { isMobile } = useDeviceType();
  const { products } = useProducts();
  const preSelectedPlanName = useTrialPaywallModalStore((state) => state.preSelectedPlanName);
  const [selectedPlanName, setSelectedPlanName] = useState<null | TrialPlan['name']>(
    preSelectedPlanName ?? PLANS[1].name,
  );
  const title = useTrialPaywallModalStore((state) => state.title);
  const [expandPreviousFeatures, setExpandPreviousFeatures] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const onClose = useTrialPaywallModalStore((state) => state.close);

  // Отправляем событие открытия пейволла один раз
  useExecuteOneTime(() => {
    MixpanelEvents.paywallOpened();
  });

  const trialPlans =
    products?.plan?.filter(
      (plan) =>
        plan.name === planNameMapping['Creator'] ||
        plan.name === planNameMapping['Creator Pro'] ||
        plan.name === planNameMapping['Business'],
    ) || [];

  const selectedPlan = PLANS.find((plan) => plan.name === selectedPlanName);

  const handleSelectPlan = (planName: TrialPlan['name']) => {
    setSelectedPlanName(planName);
  };

  const getPriceIdForSelectedPlan = (planName: string, plans: Plan[]): null | string => {
    const apiPlanName = planNameMapping[planName];
    if (!apiPlanName) return null;

    const plan = plans.find((p) => p.name === apiPlanName);
    if (!plan) return null;

    const monthlyPrice = plan.prices.find(
      (price) => price.recurring?.interval === PaymentInterval.Month && price.status === 'active',
    );

    return monthlyPrice?.id || null;
  };

  const handleContinueWithPlan = async () => {
    MixpanelEvents.continueWithPlanPaywallClick();
    if (selectedPlanName) {
      const priceId = getPriceIdForSelectedPlan(selectedPlanName, trialPlans);

      if (priceId) {
        setIsCheckoutLoading(true);

        try {
          const currentUrl = window.location.href;

          const response = await API.getBuySubscriptionCheckoutUrl({
            id: priceId,
            success_url: currentUrl,
            cancel_url: currentUrl,
          });

          window.location.href = response.checkout_url;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating checkout session:', error);
          setIsCheckoutLoading(false);
        }
      }

      onClose?.();
    }
  };

  if (isMobile) {
    return (
      <Drawer
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '90vh',
          },
        }}
        anchor="bottom"
        open
        onClose={onClose}
      >
        <Stack direction="column" sx={{ display: 'flex', height: '100%' }}>
          <ModalHeaderDefault title={title || DEFAULT_TITLE} onClose={onClose} />

          {/* Scrollable content area */}
          <Stack
            sx={{
              flex: 1,
              overflowY: 'auto',
              paddingBottom: 2,
            }}
          >
            <Stack gap={8} padding={16}>
              <PlanOptionsSection selectedPlanName={selectedPlanName} handleSelectPlan={handleSelectPlan} />
            </Stack>
            <Stack
              sx={{
                backgroundColor: 'grey.50',
              }}
              gap={20}
              padding={32}
              paddingBottom={16}
            >
              {selectedPlan?.features.map((feature) => <FeatureItem key={feature.name} feature={feature} />)}
              <PreviousFeaturesSection
                expandPreviousFeatures={expandPreviousFeatures}
                selectedPlan={selectedPlan}
                setExpandPreviousFeatures={setExpandPreviousFeatures}
              />
            </Stack>
          </Stack>

          {/* Fixed button at bottom */}
          <Stack
            sx={{
              backgroundColor: 'white',
              borderTop: '1px solid',
              borderTopColor: 'grey.200',
              bottom: 0,
              position: 'sticky',
              zIndex: 1,
            }}
            padding={16}
          >
            <Button disabled={isCheckoutLoading} fullWidth onClick={handleContinueWithPlan}>
              {isCheckoutLoading ? 'Processing...' : `Continue with ${selectedPlanName || 'Selected Plan'}`}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    );
  }

  return (
    <Modal
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
      }}
      open
      onClose={onClose}
    >
      <Stack
        direction="row"
        sx={{ backgroundColor: 'common.white', borderRadius: 16, position: 'relative' }}
        width={800}
      >
        <Box sx={{ cursor: 'pointer', position: 'absolute', right: 20, top: 28 }}>
          <Close height={24} sx={{ color: 'grey.300' }} width={24} onClick={onClose} />
        </Box>

        <Stack direction="column" justifyContent="space-between" padding={40}>
          <Stack gap={8}>
            <Typography color="grey.900" sx={{ marginBottom: 24 }} variant="title-3-md">
              {title || DEFAULT_TITLE}
            </Typography>
            <PlanOptionsSection selectedPlanName={selectedPlanName} handleSelectPlan={handleSelectPlan} />
          </Stack>
          <Button
            sx={{
              marginTop: 24,
            }}
            disabled={isCheckoutLoading}
            fullWidth
            onClick={handleContinueWithPlan}
          >
            {isCheckoutLoading ? 'Processing...' : `Continue with ${selectedPlanName || 'Selected Plan'}`}
          </Button>
        </Stack>
        <Stack
          direction="column"
          flexGrow={1}
          gap={20}
          padding={40}
          sx={{ backgroundColor: 'grey.50', borderRadius: 16 }}
        >
          {selectedPlan?.features.map((feature) => <FeatureItem key={feature.name} feature={feature} />)}

          <PreviousFeaturesSection
            expandPreviousFeatures={expandPreviousFeatures}
            selectedPlan={selectedPlan}
            setExpandPreviousFeatures={setExpandPreviousFeatures}
          />
        </Stack>
      </Stack>
    </Modal>
  );
};

/**
 * Обертка модалки TrialPaywall.
 * Рендерит содержимое только когда модалка открыта в сторе.
 */
export const TrialPaywall = () => (
  <ModalWrapper hook={useTrialPaywallModalStore}>
    <TrialPaywallComponent />
  </ModalWrapper>
);
